// src/UserControls/Payment/AddressFormModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import AddressService from "./AddressService";
import Logger from "../../utils/Logger";

// ─── Validators (mirrors PaymentPage.js VALIDATORS exactly) ──────────────────
const ADDR_VALIDATORS = {
  fullName:     (v) => (!v?.trim() ? "Full name is required." : null),
  email: (v) => {
    if (!v?.trim()) return "Email is required.";
    if (!/^\S+@\S+\.\S+$/.test(v)) return "Please enter a valid email address.";
    return null;
  },
  addressLine1: (v) => (!v?.trim() ? "Address is required." : null),
  city:         (v) => (!v?.trim() ? "City is required." : null),
  state:        (v) => (!v?.trim() ? "State is required." : null),
  postalCode: (v) => {
    if (!v?.trim()) return "ZIP code is required.";
    if (!/^\d{5}(-\d{4})?$/.test(v.trim()))
      return "Enter a valid US ZIP code (e.g. 12345 or 12345-6789).";
    return null;
  },
};

const EMPTY_FORM = {
  fullName: "", email: "",
  addressLine1: "", city: "", state: "",
  postalCode: "", country: "US", isDefault: false,
};

function validateForm(form) {
  const errors = {};
  Object.keys(ADDR_VALIDATORS).forEach((field) => {
    const err = ADDR_VALIDATORS[field](form[field]);
    if (err) errors[field] = err;
  });
  return errors;
}

// ─── Shared field helpers ─────────────────────────────────────────────────────
function FieldLabel({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
      {children}
      {required && <span className="ml-1 text-red-500 font-bold" aria-hidden="true">*</span>}
    </label>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  );
}

function inputClass(hasError) {
  const base = "w-full px-4 py-2.5 rounded-xl border text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 bg-white text-gray-900 placeholder-gray-400";
  return hasError
    ? `${base} border-red-400 focus:ring-red-400 focus:border-red-400`
    : `${base} border-blue-200 focus:ring-blue-500 focus:border-blue-500`;
}

export default function AddressFormModal({ mode = "add", address = null, onSave, onCancel }) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(() =>
    isEdit && address
      ? {
          fullName:     address.fullName     ?? "",
          email:        address.email        ?? "",
          addressLine1: address.addressLine1 ?? "",
          city:         address.city         ?? "",
          state:        address.state        ?? "",
          postalCode:   address.postalCode   ?? "",
          country:      address.country      ?? "US",
          isDefault:    address.isDefault    ?? false,
        }
      : { ...EMPTY_FORM }
  );

  const [errors, setErrors]       = useState({});
  const [touched, setTouched]     = useState({});
  const [submitAttempted, setSA]  = useState(false);
  const [saving, setSaving]       = useState(false);
  const [apiError, setApiError]   = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Re-populate form if address prop changes (e.g. switching which card to edit)
  useEffect(() => {
    if (isEdit && address) {
      setForm({
        fullName:     address.fullName     ?? "",
        email:        address.email        ?? "",
        addressLine1: address.addressLine1 ?? "",
        city:         address.city         ?? "",
        state:        address.state        ?? "",
        postalCode:   address.postalCode   ?? "",
        country:      address.country      ?? "US",
        isDefault:    address.isDefault    ?? false,
      });
      setErrors({});
      setTouched({});
      setSA(false);
      setApiError("");
      setHasChanges(false);
    }
  }, [address, isEdit]);

  const allErrors = validateForm(form);

  // Visible errors: only for touched fields, or all after submit attempt
  const visibleErrors = submitAttempted
    ? allErrors
    : Object.fromEntries(Object.entries(allErrors).filter(([k]) => touched[k]));

  const handleCancel = useCallback(() => {
    setHasChanges(false);
    onCancel();
  }, [onCancel]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setApiError("");
    setHasChanges(true);
  }, []);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleSave = async () => {
    setSA(true);
    setApiError("");

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const userId = await AddressService.getUserId();

      if (isEdit) {
        // Edit mode — PUT
        await AddressService.updateAddress(address.addressId, userId, form);
        const updatedAddress = { ...address, ...form };
        Logger.info("✅ Address updated via modal", { addressId: address.addressId });
        setHasChanges(false);
        onSave(updatedAddress);
      } else {
        // Add mode — POST
        const result = await AddressService.createAddress(userId, form);
        const newAddress = { ...form, addressId: result.addressId };
        Logger.info("✅ Address created via modal", { addressId: result.addressId });
        setHasChanges(false);
        onSave(newAddress);
      }
    } catch (err) {
      Logger.error("❌ AddressFormModal save failed", { error: err });
      setApiError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && !saving) handleCancel(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [saving, handleCancel]);

  // Handle beforeunload event for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        const confirmMessage = "You have unsaved changes. If you reload, these changes will be lost. Do you want to reload?";
        e.preventDefault();
        e.returnValue = confirmMessage;
        
        // Show browser confirmation dialog
        const shouldReload = window.confirm(confirmMessage + "\n\nClick 'OK' to reload and discard changes, or 'Cancel' to stay on this page.");
        if (shouldReload) {
          // User chose to reload - clear the form changes
          setHasChanges(false);
          window.location.reload();
        } else {
          // User chose to cancel - prevent reload
          e.preventDefault();
        }
      }
    };

    if (hasChanges) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [hasChanges]);

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) handleCancel(); }}
    >
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ animation: "slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}
      >
        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(32px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0)   scale(1); }
          }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEdit ? "Edit Address" : "Add New Address"}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">US addresses only</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !saving && handleCancel()}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* API error banner */}
        {apiError && (
          <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-700 font-medium">{apiError}</p>
          </div>
        )}

        {/* Form body */}
        <div className="px-8 py-6 space-y-5">

          {/* Full Name */}
          <div>
            <FieldLabel htmlFor="af-fullName" required>Full Name</FieldLabel>
            <input
              id="af-fullName"
              type="text"
              autoComplete="name"
              placeholder="John Doe"
              value={form.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              onBlur={() => handleBlur("fullName")}
              className={inputClass(!!visibleErrors.fullName)}
              aria-invalid={!!visibleErrors.fullName}
            />
            <FieldError message={visibleErrors.fullName} />
          </div>

          {/* Email */}
          <div>
            <FieldLabel htmlFor="af-email" required>Email</FieldLabel>
            <input
              id="af-email"
              type="email"
              autoComplete="email"
              placeholder="john@example.com"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              className={inputClass(!!visibleErrors.email)}
              aria-invalid={!!visibleErrors.email}
            />
            <FieldError message={visibleErrors.email} />
          </div>

          {/* Address Line 1 */}
          <div>
            <FieldLabel htmlFor="af-address" required>Street Address</FieldLabel>
            <input
              id="af-address"
              type="text"
              autoComplete="street-address"
              placeholder="123 Main Street"
              value={form.addressLine1}
              onChange={(e) => handleChange("addressLine1", e.target.value)}
              onBlur={() => handleBlur("addressLine1")}
              className={inputClass(!!visibleErrors.addressLine1)}
              aria-invalid={!!visibleErrors.addressLine1}
            />
            <FieldError message={visibleErrors.addressLine1} />
          </div>

          {/* City + State */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="af-city" required>City</FieldLabel>
              <input
                id="af-city"
                type="text"
                autoComplete="address-level2"
                placeholder="New York"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                onBlur={() => handleBlur("city")}
                className={inputClass(!!visibleErrors.city)}
                aria-invalid={!!visibleErrors.city}
              />
              <FieldError message={visibleErrors.city} />
            </div>
            <div>
              <FieldLabel htmlFor="af-state" required>State</FieldLabel>
              <input
                id="af-state"
                type="text"
                autoComplete="address-level1"
                placeholder="NY"
                value={form.state}
                onChange={(e) => handleChange("state", e.target.value)}
                onBlur={() => handleBlur("state")}
                className={inputClass(!!visibleErrors.state)}
                aria-invalid={!!visibleErrors.state}
              />
              <FieldError message={visibleErrors.state} />
            </div>
          </div>

          {/* ZIP + Country */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="af-zip" required>ZIP Code</FieldLabel>
              <input
                id="af-zip"
                type="text"
                autoComplete="postal-code"
                placeholder="10001"
                maxLength={10}
                value={form.postalCode}
                onChange={(e) => handleChange("postalCode", e.target.value)}
                onBlur={() => handleBlur("postalCode")}
                className={inputClass(!!visibleErrors.postalCode)}
                aria-invalid={!!visibleErrors.postalCode}
              />
              <FieldError message={visibleErrors.postalCode} />
            </div>
            <div>
              <FieldLabel htmlFor="af-country" required>Country</FieldLabel>
              <select
                id="af-country"
                value="US"
                disabled
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-blue-50 text-sm text-gray-700 cursor-not-allowed appearance-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="US">United States (US)</option>
              </select>
              <p className="mt-1 text-xs text-blue-600 font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm1 4a1 1 0 00-2 0v4a1 1 0 002 0v-4z" clipRule="evenodd" />
                </svg>
                US only
              </p>
            </div>
          </div>

          {/* Set as default checkbox */}
          <label className="flex items-center gap-2 cursor-pointer group">
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only"
                checked={form.isDefault}
                onChange={(e) => handleChange("isDefault", e.target.checked)}
              />
              <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center transition-all ${
                form.isDefault
                  ? "bg-blue-600 border-blue-600"
                  : "border-gray-300 group-hover:border-blue-400"
              }`}>
                {form.isDefault && (
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-xs text-gray-700 font-medium">
              {isEdit ? "Set as default address" : "Save as default address"}
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-8 pb-8 pt-2">
          <button
            type="button"
            onClick={() => !saving && handleCancel()}
            disabled={saving}
            className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || (isEdit ? (!hasChanges || Object.keys(allErrors).length > 0) : Object.keys(allErrors).length > 0)}
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving…
              </>
            ) : (
              <>{isEdit ? "Save Changes" : "Add Address"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}